import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import RovoChatIcon from '@atlaskit/icon/core/rovo-chat';
import { Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { FollowUpQuestions } from './FollowUpQuestions.tsx';
import { RovoInput } from './RovoInput.tsx';
import { SourcesSection } from './SourcesSection.tsx';
import type { PopularQuestion } from './popularQuestionsTypes.ts';

type QuestionItemProps = {
	question: PopularQuestion;
	isExpanded: boolean;
	onToggle: (id: string) => void;
	onFollowUp?: (followUpQuestion: string, parentQuestion: PopularQuestion) => void;
	onAskRovo?: (customQuestion: string, parentQuestion: PopularQuestion) => void;
};

const QuestionHeader = ({
	questionText,
	isExpanded,
	onToggle,
}: {
	questionText: string;
	isExpanded: boolean;
	onToggle: () => void;
}) => {
	const { formatMessage } = useIntl();

	return (
		<Pressable
			xcss={cx(styles.questionRow, isExpanded && styles.questionRowExpanded)}
			onClick={onToggle}
			aria-expanded={isExpanded}
		>
			<div css={styles.questionIcon}>
				<RovoChatIcon label="" color={token('color.icon.subtle')} />
			</div>
			<div css={styles.questionText}>
				<Text size="medium" color="color.text.subtle">
					{questionText}
				</Text>
			</div>
			<div css={styles.chevron}>
				{isExpanded ? (
					<ChevronUpIcon label={formatMessage(i18n.collapse)} color={token('color.icon.subtle')} />
				) : (
					<ChevronDownIcon label={formatMessage(i18n.expand)} color={token('color.icon.subtle')} />
				)}
			</div>
		</Pressable>
	);
};

export const QuestionItem = ({
	question,
	isExpanded,
	onToggle,
	onFollowUp,
	onAskRovo,
}: QuestionItemProps) => {
	const { formatMessage } = useIntl();
	const [sourcesExpanded, setSourcesExpanded] = useState(false);

	const handleToggle = useCallback(() => {
		onToggle(question.id);
	}, [onToggle, question.id]);

	const handleFollowUp = useCallback(
		(followUpQuestion: string) => {
			onFollowUp?.(followUpQuestion, question);
		},
		[onFollowUp, question],
	);

	const handleAskRovo = useCallback(
		(message: string) => {
			onAskRovo?.(message, question);
		},
		[onAskRovo, question],
	);

	const toggleSources = useCallback(() => {
		setSourcesExpanded((prev) => !prev);
	}, []);

	return (
		<div css={styles.cardWrapper}>
			<QuestionHeader
				questionText={question.question}
				isExpanded={isExpanded}
				onToggle={handleToggle}
			/>

			{isExpanded && (
				<div css={styles.expandedContent}>
					<Stack space="space.200">
						<Stack space="space.100">
							<div css={styles.sectionLabel}>
								<Text size="small" weight="medium" color="color.text.subtlest">
									{formatMessage(i18n.answerLabel)}
								</Text>
							</div>
							<Text size="medium" color="color.text.subtle">
								{question.answer}
							</Text>
						</Stack>

						<div>
							<SourcesSection
								sources={question.sources}
								isExpanded={sourcesExpanded}
								onToggle={toggleSources}
							/>

							<FollowUpQuestions
								questions={question.followUpQuestions}
								onFollowUp={handleFollowUp}
							/>
						</div>

						<RovoInput onSend={handleAskRovo} />
					</Stack>
				</div>
			)}
		</div>
	);
};

const i18n = defineMessages({
	expand: {
		id: 'space-overview.smart-space-overview.popular-questions.expand',
		defaultMessage: 'Expand question',
		description: 'Accessible label for the button to expand a popular question.',
	},
	collapse: {
		id: 'space-overview.smart-space-overview.popular-questions.collapse',
		defaultMessage: 'Collapse question',
		description: 'Accessible label for the button to collapse a popular question.',
	},
	answerLabel: {
		id: 'space-overview.smart-space-overview.popular-questions.answer-label',
		defaultMessage: 'Answer',
		description: 'Label for the answer section of a popular question.',
	},
});

const styles = cssMap({
	cardWrapper: {
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface.sunken'),
		overflow: 'hidden',
	},
	questionRow: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.250'),
		cursor: 'pointer',
		backgroundColor: token('elevation.surface.sunken'),
		borderWidth: '0',
		width: '100%',
		font: token('font.body'),
		color: token('color.text'),
		textAlign: 'start',
		borderRadius: token('radius.xlarge'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	questionRowExpanded: {
		borderEndStartRadius: '0',
		borderEndEndRadius: '0',
	},
	questionIcon: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	questionText: {
		flex: 1,
		minWidth: '0px',
	},
	chevron: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	expandedContent: {
		backgroundColor: token('elevation.surface.sunken'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.250'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	sectionLabel: {
		paddingBlockEnd: token('space.050'),
	},
});
